import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { MagnifyingGlass, Cube, User, FolderPlus, DownloadSimple } from "@phosphor-icons/react";
import { useEscStep } from "@/lib/escBack";
import StockLedger from "./StockLedger";
import AccountMaster from "./AccountMaster";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Unified Ledger page — hosts two sub-tabs:
 *   • Stock Ledger    — inventory movements (existing, delegated)
 *   • Customer Ledger — party account view (sales + purchases + notes + payments)
 *                       with running balance, FY/month grouping, outstanding.
 */
export default function Ledger() {
  const [tab, setTab] = useState("master"); // "master" | "stock" | "customer"
  return (
    <div className="p-6 space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-black tracking-tight">Ledger</h1>
          <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mt-1">
            Track stock movements and party balances in one place
          </p>
        </div>
        {/* Sub-tab switcher */}
        <div className="flex gap-1 bg-slate-100 p-1 rounded-sm" data-testid="ledger-subtab-picker">
          <button
            data-testid="ledger-tab-master"
            onClick={() => setTab("master")}
            className={`px-4 py-2 text-xs font-semibold rounded-sm transition-all flex items-center gap-2 ${
              tab === "master" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-900"
            }`}
          >
            <FolderPlus size={16} /> Create Ledger
          </button>
          <button
            data-testid="ledger-tab-stock"
            onClick={() => setTab("stock")}
            className={`px-4 py-2 text-xs font-semibold rounded-sm transition-all flex items-center gap-2 ${
              tab === "stock" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-900"
            }`}
          >
            <Cube size={16} /> Stock Ledger
          </button>
          <button
            data-testid="ledger-tab-customer"
            onClick={() => setTab("customer")}
            className={`px-4 py-2 text-xs font-semibold rounded-sm transition-all flex items-center gap-2 ${
              tab === "customer" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-900"
            }`}
          >
            <User size={16} /> Customer Ledger
          </button>
        </div>
      </div>

      {tab === "master" ? (
        <AccountMaster />
      ) : tab === "stock" ? (
        // Stock ledger has its own header — hide our outer wrapper padding
        // and just render it inline.
        <div className="-mx-6 -my-6"><StockLedger /></div>
      ) : (
        <CustomerLedgerTab />
      )}
    </div>
  );
}

// ============================================================== //
// Customer Ledger sub-tab
// ============================================================== //
function CustomerLedgerTab() {
  const [query, setQuery] = useState("");
  const [suggest, setSuggest] = useState([]);
  const [selected, setSelected] = useState(null); // {id, name}
  const [ledger, setLedger] = useState(null);
  const [overview, setOverview] = useState(null);
  const [mode, setMode] = useState("fy"); // "fy" | "month" | "range"
  const [fyStart, setFyStart] = useState(() => {
    const d = new Date();
    return d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1;
  });
  const [month, setMonth] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [loading, setLoading] = useState(false);

  // Debounced typeahead — quick /customer-ledger search hits every keystroke.
  useEffect(() => {
    let alive = true;
    const t = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/customer-ledger`, { params: { q: query, limit: 15 } });
        if (alive) setSuggest(r.data || []);
      } catch {
        /* silent */
      }
    }, 200);
    return () => { alive = false; clearTimeout(t); };
  }, [query]);

  const buildParams = () => {
    if (mode === "fy") return { fy: fyStart };
    if (mode === "month" && month) return { month };
    if (mode === "range") return { from_date: fromDate || undefined, to_date: toDate || undefined };
    return {};
  };

  const fetchLedger = async () => {
    if (!selected) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/customer-ledger/${encodeURIComponent(selected.id)}`, { params: buildParams() });
      setLedger(r.data);
    } catch (e) {
      alert("Failed to load ledger: " + (e.response?.data?.detail || e.message));
    } finally { setLoading(false); }
  };

  const adoptStray = async (supplierName) => {
    if (!selected) return;
    if (!window.confirm(`Link all bills of "${supplierName}" to this party?`)) return;
    try {
      const r = await axios.post(`${API}/customer-ledger/${encodeURIComponent(selected.id)}/adopt-bills`, { supplier_name: supplierName });
      alert(`${r.data.updated} bill(s) linked to ${r.data.party}`);
      fetchLedger();
    } catch (e) {
      alert("Failed: " + (e.response?.data?.detail || e.message));
    }
  };

  useEffect(() => {
    if (selected) fetchLedger();
  }, [selected, mode, fyStart, month, fromDate, toDate]); // eslint-disable-line react-hooks/exhaustive-deps

  // Overview — all parties + payable/receivable summary (shown when no party selected)
  const fetchOverview = () => {
    axios.get(`${API}/customer-ledger/overview`).then((r) => setOverview(r.data)).catch(() => {});
  };
  useEffect(() => { fetchOverview(); }, []);
  useEffect(() => { if (!selected) fetchOverview(); }, [selected]); // eslint-disable-line react-hooks/exhaustive-deps

  // Instant freshness: re-fetch whenever the user returns to this tab/window
  // so bills added elsewhere (sale/purchase forms, other tabs) show up
  // immediately without a manual page reload.
  useEffect(() => {
    const onFocus = () => {
      if (document.visibilityState && document.visibilityState !== "visible") return;
      if (selected) fetchLedger(); else fetchOverview();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [selected, mode, fyStart, month, fromDate, toDate]); // eslint-disable-line react-hooks/exhaustive-deps

  // Esc = one step back: party detail → overview list (not full page back)
  useEscStep(() => {
    if (selected) { setSelected(null); setLedger(null); setQuery(""); return true; }
    return false;
  });

  const downloadFile = async (url, filename) => {
    try {
      const res = await axios.get(url, { responseType: "blob" });
      const objUrl = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a"); link.href = objUrl; link.setAttribute("download", filename);
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(objUrl);
    } catch (err) { alert("Download failed: " + (err.response?.data?.detail || err.message)); }
  };

  const exportParty = (p, kind, withPeriod = false) => {
    const params = new URLSearchParams();
    if (withPeriod) Object.entries(buildParams()).forEach(([k, v]) => { if (v) params.set(k, v); });
    const q = params.toString();
    downloadFile(`${API}/customer-ledger/${encodeURIComponent(p.id)}/${kind}${q ? `?${q}` : ""}`,
      `Ledger_${(p.name || "party").replace(/\s+/g, "_")}.${kind === "excel" ? "xlsx" : "pdf"}`);
  };

  // Group events by FY → month for the display panel.
  const grouped = useMemo(() => {
    if (!ledger?.events) return [];
    const map = new Map(); // fy -> Map(month -> events[])
    for (const e of ledger.events) {
      const [y, m] = e.date.slice(0, 7).split("-").map(Number);
      const fyOfMonth = m >= 4 ? y : y - 1;
      const fyLabel = `FY ${fyOfMonth}-${String((fyOfMonth + 1) % 100).padStart(2, "0")}`;
      const mk = e.date.slice(0, 7);
      if (!map.has(fyLabel)) map.set(fyLabel, new Map());
      const mm = map.get(fyLabel);
      if (!mm.has(mk)) mm.set(mk, []);
      mm.get(mk).push(e);
    }
    // Convert to array with ordered keys
    return [...map.entries()].map(([fyLabel, mm]) => ({
      fyLabel,
      months: [...mm.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([mk, events]) => ({ mk, events })),
    }));
  }, [ledger]);

  const monthName = (mk) => {
    try {
      const [y, m] = mk.split("-").map(Number);
      return new Date(y, m - 1, 1).toLocaleString("en-IN", { month: "long", year: "numeric" });
    } catch { return mk; }
  };

  const typeColor = (t) => ({
    sale_invoice: "text-blue-700 bg-blue-50 border-blue-200",
    payment_received: "text-emerald-700 bg-emerald-50 border-emerald-200",
    credit_note: "text-purple-700 bg-purple-50 border-purple-200",
    debit_note: "text-rose-700 bg-rose-50 border-rose-200",
    purchase_bill: "text-orange-700 bg-orange-50 border-orange-200",
    purchase_debit_note: "text-rose-700 bg-rose-50 border-rose-200",
    purchase_credit_note: "text-purple-700 bg-purple-50 border-purple-200",
    bank_settled: "text-teal-700 bg-teal-50 border-teal-200",
    payment_given: "text-emerald-700 bg-emerald-50 border-emerald-200",
    voucher_receipt: "text-emerald-700 bg-emerald-50 border-emerald-200",
    voucher_payment: "text-amber-700 bg-amber-50 border-amber-200",
    opening_balance: "text-slate-800 bg-[#f6efdd] border-[#d8ccae]",
  }[t] || "text-slate-700 bg-slate-50 border-slate-200");

  return (
    <div className="space-y-4">
      {/* Search + filter bar */}
      <div className="bg-white border border-slate-200 rounded-sm p-4 flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[240px] relative">
          <label className="text-xs font-bold uppercase tracking-wider text-slate-500 block mb-2">Search Customer / Supplier</label>
          <div className="relative">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="customer-ledger-search"
              type="text"
              value={query}
              onChange={(e) => { setQuery(e.target.value); setSelected(null); }}
              placeholder="Type party name…"
              className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            />
          </div>
          {query && !selected && suggest.length > 0 && (
            <div className="absolute z-10 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-sm shadow-lg max-h-60 overflow-y-auto">
              {suggest.map((s) => (
                <button
                  key={s.id}
                  data-testid={`customer-suggest-${s.id}`}
                  onClick={() => { setSelected(s); setQuery(s.name); setSuggest([]); }}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-slate-100 border-b border-slate-100 last:border-0"
                >
                  <div className="font-semibold text-slate-900 flex items-center gap-2">
                    {s.name}
                    {s.party_type === "purchase" && <span className="text-[9px] font-black px-1.5 py-0.5 rounded-sm bg-indigo-100 text-indigo-800">PURCHASE</span>}
                  </div>
                  <div className="text-xs text-slate-500">
                    {s.gstin || "No GSTIN"} · {s.state || "—"} {s.phone ? `· ${s.phone}` : ""}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="flex gap-1 bg-slate-100 p-1 rounded-sm">
          {[
            ["fy", "FY"], ["month", "Month"], ["range", "Custom Range"],
          ].map(([v, lbl]) => (
            <button
              key={v}
              data-testid={`ledger-period-${v}`}
              onClick={() => setMode(v)}
              className={`px-3 py-1.5 text-xs font-semibold rounded-sm ${mode === v ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-900"}`}
            >{lbl}</button>
          ))}
        </div>

        {mode === "fy" && (
          <select
            data-testid="ledger-fy-select"
            value={fyStart}
            onChange={(e) => setFyStart(Number(e.target.value))}
            className="px-3 py-2 border border-slate-200 rounded-sm text-sm font-semibold"
          >
            {Array.from({ length: 8 }, (_, i) => {
              const y = new Date().getFullYear() + 1 - i;
              return <option key={y} value={y}>{`FY ${y}-${String((y + 1) % 100).padStart(2, "0")}`}</option>;
            })}
          </select>
        )}
        {mode === "month" && (
          <input data-testid="ledger-month-input" type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="px-3 py-2 border border-slate-200 rounded-sm text-sm font-semibold" />
        )}
        {mode === "range" && (
          <>
            <input data-testid="ledger-from-date" type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="px-3 py-2 border border-slate-200 rounded-sm text-sm font-semibold" />
            <span className="text-xs text-slate-500">to</span>
            <input data-testid="ledger-to-date" type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="px-3 py-2 border border-slate-200 rounded-sm text-sm font-semibold" />
          </>
        )}
      </div>

      {!selected && !overview && (
        <div className="bg-slate-50 border border-slate-200 rounded-sm p-10 text-center">
          <User size={40} className="mx-auto mb-3 text-slate-300" />
          <p className="text-sm font-semibold text-slate-500">Search and select a customer or supplier above to view their ledger.</p>
        </div>
      )}

      {!selected && overview && (
        <div className="space-y-4" data-testid="ledger-overview">
          {/* All-parties export */}
          <div className="flex justify-end gap-2">
            <button data-testid="ledger-overview-excel"
              onClick={() => downloadFile(`${API}/customer-ledger/overview/excel`, "CustomerLedger_AllParties.xlsx")}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-emerald-700 hover:text-white hover:bg-emerald-700 border border-emerald-400 rounded-sm transition-colors">
              <DownloadSimple size={13} /> Excel
            </button>
            <button data-testid="ledger-overview-pdf"
              onClick={() => downloadFile(`${API}/customer-ledger/overview/pdf`, "CustomerLedger_AllParties.pdf")}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-rose-700 hover:text-white hover:bg-rose-700 border border-rose-400 rounded-sm transition-colors">
              <DownloadSimple size={13} /> PDF
            </button>
          </div>
          {/* Payable / Receivable / Net summary */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="bg-white border-l-4 border-l-rose-500 border border-slate-200 rounded-sm p-4">
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Payable Amount (You Owe — Suppliers)</p>
              <p className="text-2xl font-black text-rose-700 mt-1" data-testid="overview-payable">₹{Number(overview.totals.payable || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</p>
            </div>
            <div className="bg-white border-l-4 border-l-emerald-500 border border-slate-200 rounded-sm p-4">
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Receivable Amount (They Owe — Customers)</p>
              <p className="text-2xl font-black text-emerald-700 mt-1" data-testid="overview-receivable">₹{Number(overview.totals.receivable || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</p>
            </div>
            <div className="bg-slate-900 rounded-sm p-4">
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Total Balance (Receivable − Payable)</p>
              <p className={`text-2xl font-black mt-1 ${Number(overview.totals.net || 0) >= 0 ? "text-emerald-400" : "text-rose-400"}`} data-testid="overview-net">₹{Number(overview.totals.net || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</p>
            </div>
          </div>

          <PartyListTable
            title="Purchase Ledgers — Suppliers (Sundry Creditors)"
            accent="rose"
            rows={overview.purchase_parties}
            amountLabel="Paid"
            balanceLabel="Payable"
            onPick={(r) => { setSelected({ id: r.id, name: r.name, party_type: "purchase" }); setQuery(r.name); }}
            onExport={exportParty}
            testid="overview-purchase-ledgers"
          />
          <PartyListTable
            title="Sales Ledgers — Customers (Sundry Debtors)"
            accent="emerald"
            rows={overview.sales_parties}
            amountLabel="Received"
            balanceLabel="Receivable"
            onPick={(r) => { setSelected({ id: r.id, name: r.name, party_type: "sales" }); setQuery(r.name); }}
            onExport={exportParty}
            testid="overview-sales-ledgers"
          />
        </div>
      )}

      {selected && loading && <div className="text-center py-10 text-sm text-slate-500">Loading ledger…</div>}

      {selected && ledger && !loading && (
        <>
          {/* Party header + export */}
          <div className="flex items-center justify-between flex-wrap gap-2">
            <p className="text-sm font-black text-slate-900">{ledger.customer?.name || selected.name}
              <span className="ml-2 text-[10px] font-bold uppercase text-slate-400">{ledger.period_label}</span>
            </p>
            <div className="flex gap-2">
              <button data-testid="party-ledger-excel"
                onClick={() => exportParty({ id: selected.id, name: ledger.customer?.name || selected.name }, "excel", true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-emerald-700 hover:text-white hover:bg-emerald-700 border border-emerald-400 rounded-sm transition-colors">
                <DownloadSimple size={13} /> Excel
              </button>
              <button data-testid="party-ledger-pdf"
                onClick={() => exportParty({ id: selected.id, name: ledger.customer?.name || selected.name }, "pdf", true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-rose-700 hover:text-white hover:bg-rose-700 border border-rose-400 rounded-sm transition-colors">
                <DownloadSimple size={13} /> PDF
              </button>
            </div>
          </div>
          {/* Totals cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <SummaryCard label="Total Sales" value={ledger.totals.total_sales} color="text-blue-700" />
            <SummaryCard label="Total Purchases" value={ledger.totals.total_purchases} color="text-orange-700" />
            <SummaryCard label="Payments Received" value={ledger.totals.total_payments_received} color="text-emerald-700" />
            <SummaryCard label={ledger.totals.outstanding_label} value={Math.abs(ledger.totals.outstanding)} color={ledger.totals.outstanding > 0 ? "text-red-700" : (ledger.totals.outstanding < 0 ? "text-purple-700" : "text-slate-500")} />
            <SummaryCard label="Period" value={ledger.period_label} isText color="text-slate-700" />
          </div>

          {/* Stray bills — purchases saved under a similar-but-different supplier name */}
          {ledger.stray_bills?.length > 0 && (
            <div className="bg-amber-50 border border-amber-300 rounded-sm p-4" data-testid="stray-bills-panel">
              <p className="text-sm font-black text-amber-900 mb-1">
                Missing bills found under a different supplier name
              </p>
              <p className="text-xs text-amber-700 mb-3">
                These purchase bills are in your company but are NOT linked to this party.
                If they belong to this party, click "Link to this party" — they will instantly appear in this ledger.
              </p>
              <div className="space-y-2">
                {ledger.stray_bills.map((g, i) => (
                  <div key={i} data-testid={`stray-bill-group-${i}`}
                    className="flex flex-wrap items-center gap-3 bg-white border border-amber-200 rounded-sm px-3 py-2">
                    <span className="text-sm font-bold text-slate-900">{g.supplier_name}</span>
                    <span className="text-xs text-slate-500">{g.count} bill{g.count > 1 ? "s" : ""} · ₹{Number(g.total).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
                    <button
                      type="button"
                      data-testid={`adopt-stray-${i}`}
                      onClick={() => adoptStray(g.supplier_name)}
                      className="ml-auto px-3 py-1 text-xs font-bold text-amber-900 border border-amber-500 rounded-sm hover:bg-amber-500 hover:text-white transition-colors">
                      Link to this party
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Monthly summary */}
          {ledger.monthly?.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-sm sticky-table">
              <div className="px-4 py-2 bg-slate-50 border-b border-slate-200">
                <p className="text-xs font-bold uppercase tracking-widest text-slate-500">FY / Month Summary</p>
              </div>
              <table className="w-full text-sm" data-testid="customer-ledger-monthly">
                <thead className="sticky-thead bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
                  <tr className="border-b border-slate-200">
                    <th className="px-4 py-2 text-left">Month</th>
                    <th className="px-4 py-2 text-left">FY</th>
                    <th className="px-4 py-2 text-right">Entries</th>
                    <th className="px-4 py-2 text-right">Debit</th>
                    <th className="px-4 py-2 text-right">Credit</th>
                    <th className="px-4 py-2 text-right">Closing Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {ledger.monthly.map((m) => (
                    <tr key={m.month} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                      <td className="px-4 py-2 font-semibold">{monthName(m.month)}</td>
                      <td className="px-4 py-2 text-slate-500 text-xs">{m.fy}</td>
                      <td className="px-4 py-2 text-right">{m.count}</td>
                      <td className="px-4 py-2 text-right text-red-700 font-mono">{m.debit.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
                      <td className="px-4 py-2 text-right text-emerald-700 font-mono">{m.credit.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
                      <td className={`px-4 py-2 text-right font-mono font-bold ${m.closing_balance > 0 ? "text-red-700" : (m.closing_balance < 0 ? "text-purple-700" : "text-slate-500")}`}>
                        {m.closing_balance.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Grouped event list */}
          {grouped.map(({ fyLabel, months }) => (
            <div key={fyLabel} className="bg-white border border-slate-200 rounded-sm sticky-table">
              <div className="px-4 py-2 bg-blue-50 border-b border-blue-200 flex items-center justify-between">
                <p className="text-sm font-black text-blue-900">{fyLabel}</p>
                <p className="text-xs text-blue-700">{months.reduce((s, m) => s + m.events.length, 0)} transactions</p>
              </div>
              {months.map(({ mk, events }) => (
                <div key={mk}>
                  <div className="px-4 py-1.5 bg-slate-50 border-b border-slate-200">
                    <p className="text-xs font-bold uppercase tracking-widest text-slate-600">{monthName(mk)}</p>
                  </div>
                  <table className="w-full text-sm" data-testid={`ledger-month-${mk}`}>
                    <thead className="sticky-thead bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500">
                      <tr className="border-b border-slate-200">
                        <th className="px-3 py-2 text-left w-24">Date</th>
                        <th className="px-3 py-2 text-left w-32">Type</th>
                        <th className="px-3 py-2 text-left w-32">Document</th>
                        <th className="px-3 py-2 text-left">Description</th>
                        <th className="px-3 py-2 text-right w-24">Debit</th>
                        <th className="px-3 py-2 text-right w-24">Credit</th>
                        <th className="px-3 py-2 text-right w-28">Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {events.map((e, i) => (
                        <tr key={`${e.type}-${e.ref_id}-${i}`} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                          <td className="px-3 py-2 font-mono text-xs">{e.date.slice(0, 10)}</td>
                          <td className="px-3 py-2">
                            <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm border ${typeColor(e.type)}`}>
                              {e.type.replace(/_/g, " ")}
                            </span>
                          </td>
                          <td className="px-3 py-2 font-mono text-xs font-semibold">{e.doc_no}</td>
                          <td className="px-3 py-2 text-slate-600">
                            {e.description}
                            {e.type === "sale_invoice" && (
                              <span className={`ml-2 text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-sm ${
                                e.status === "paid" ? "bg-emerald-100 text-emerald-700"
                                : e.status === "partial" ? "bg-amber-100 text-amber-700"
                                : "bg-red-100 text-red-700"
                              }`}>
                                {e.status || "unpaid"}{e.amount_paid > 0 && e.status !== "paid" ? ` · ₹${e.amount_paid.toFixed(0)} paid` : ""}
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-right font-mono text-red-700">{e.debit > 0 ? e.debit.toLocaleString("en-IN", { minimumFractionDigits: 2 }) : ""}</td>
                          <td className="px-3 py-2 text-right font-mono text-emerald-700">{e.credit > 0 ? e.credit.toLocaleString("en-IN", { minimumFractionDigits: 2 }) : ""}</td>
                          <td className={`px-3 py-2 text-right font-mono font-bold ${e.balance > 0 ? "text-red-700" : (e.balance < 0 ? "text-purple-700" : "text-slate-500")}`}>
                            {e.balance.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          ))}

          {ledger.events.length === 0 && (
            <div className="bg-slate-50 border border-slate-200 rounded-sm p-8 text-center text-sm text-slate-500">
              No transactions found for {selected.name} in {ledger.period_label}.
            </div>
          )}

          {ledger.events.length > 0 && (
            <div className="bg-slate-900 text-white rounded-sm p-4 flex flex-wrap items-center gap-x-8 gap-y-2" data-testid="ledger-closing-summary">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Total Sales</p>
                <p className="text-base font-black">₹{Number(ledger.totals.total_sales || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</p>
              </div>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Total Purchases</p>
                <p className="text-base font-black">₹{Number(ledger.totals.total_purchases || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</p>
              </div>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-400">Payment Received (Total)</p>
                <p className="text-base font-black text-emerald-400">₹{Number(ledger.totals.total_received_all ?? ledger.totals.total_payments_received ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</p>
                {Number(ledger.totals.bank_received || 0) > 0 && (
                  <p className="text-[10px] text-slate-400">incl. ₹{Number(ledger.totals.bank_received).toLocaleString("en-IN")} bank on-account</p>
                )}
              </div>
              {Number(ledger.totals.bank_paid || 0) > 0 && (
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Paid via Bank</p>
                  <p className="text-base font-black">₹{Number(ledger.totals.bank_paid).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</p>
                </div>
              )}
              <div className="ml-auto text-right">
                <p className="text-[10px] font-bold uppercase tracking-widest text-amber-400">{ledger.totals.outstanding_label}</p>
                <p className="text-xl font-black text-amber-400">₹{Math.abs(Number(ledger.totals.outstanding || 0)).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</p>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

const PartyListTable = ({ title, accent, rows, amountLabel, balanceLabel, onPick, onExport, testid }) => (
  <div className="bg-white border border-slate-200 rounded-sm sticky-table" data-testid={testid}>
    <div className={`px-4 py-2 border-b ${accent === "rose" ? "bg-rose-50 border-rose-200" : "bg-emerald-50 border-emerald-200"}`}>
      <p className={`text-xs font-black uppercase tracking-widest ${accent === "rose" ? "text-rose-800" : "text-emerald-800"}`}>{title} · {rows.length}</p>
    </div>
    <table className="w-full text-sm">
      <thead className="sticky-thead bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500">
        <tr className="border-b border-slate-200">
          <th className="px-4 py-2 text-left">Party</th>
          <th className="px-4 py-2 text-left">Ledger Group</th>
          <th className="px-4 py-2 text-right">Bills</th>
          <th className="px-4 py-2 text-right">Billed</th>
          <th className="px-4 py-2 text-right">{amountLabel}</th>
          <th className="px-4 py-2 text-right">DN/CN Adj</th>
          <th className="px-4 py-2 text-right">{balanceLabel}</th>
          <th className="px-4 py-2 text-right">Download</th>
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 ? (
          <tr><td colSpan={8} className="px-4 py-6 text-center text-slate-400 text-xs">No parties yet.</td></tr>
        ) : rows.map((r) => (
          <tr key={r.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50 cursor-pointer"
            data-testid={`overview-party-${r.id}`} onClick={() => onPick(r)}>
            <td className="px-4 py-2 font-semibold text-slate-900">{r.name}{r.gstin ? <span className="ml-2 text-[10px] text-slate-400 font-mono">{r.gstin}</span> : null}</td>
            <td className="px-4 py-2 text-xs"><span className="inline-block px-2 py-0.5 rounded-sm bg-slate-100 text-slate-600 font-bold">{r.group_name}</span></td>
            <td className="px-4 py-2 text-right">{r.bills}</td>
            <td className="px-4 py-2 text-right font-mono">{Number(r.billed || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
            <td className="px-4 py-2 text-right font-mono text-emerald-700">{Number((amountLabel === "Paid" ? r.paid : r.received) || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
            <td className="px-4 py-2 text-right font-mono text-purple-700">{Number(r.notes_adj || 0) !== 0 ? Number(r.notes_adj).toLocaleString("en-IN", { minimumFractionDigits: 2 }) : ""}</td>
            <td className={`px-4 py-2 text-right font-mono font-black ${Number(r.balance) > 0 ? (accent === "rose" ? "text-rose-700" : "text-emerald-700") : "text-slate-500"}`}>
              {Number(r.balance || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
            </td>
            <td className="px-4 py-2 text-right whitespace-nowrap">
              <button onClick={(ev) => { ev.stopPropagation(); onExport(r, "excel"); }}
                data-testid={`party-excel-${r.id}`} title="Excel download"
                className="inline-flex items-center gap-1 px-2 py-1 mr-1.5 text-[10px] font-bold text-emerald-700 hover:text-white hover:bg-emerald-700 border border-emerald-400 rounded-sm transition-colors">
                <DownloadSimple size={10} /> Excel
              </button>
              <button onClick={(ev) => { ev.stopPropagation(); onExport(r, "pdf"); }}
                data-testid={`party-pdf-${r.id}`} title="PDF download"
                className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-bold text-rose-700 hover:text-white hover:bg-rose-700 border border-rose-400 rounded-sm transition-colors">
                <DownloadSimple size={10} /> PDF
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const SummaryCard = ({ label, value, color, isText }) => (
  <div className="bg-white border border-slate-200 rounded-sm p-3">
    <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{label}</p>
    <p className={`text-lg font-black mt-1 ${color}`}>
      {isText ? value : `₹${Number(value || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`}
    </p>
  </div>
);
